import { NavLink, Link } from "react-router-dom";
import { useAuth, hasAnyRole, isMaster, isCoworking } from "@/context/AuthContext";
import {
    DoorOpen, GaugeCircle, Users2, UserCog, CalendarDays,
    BookCheck, Megaphone, Inbox, Wrench, Kanban, Ticket, ClipboardList,
    UserPlus, Sun, Settings, Home, SlidersHorizontal, Package,
} from "lucide-react";

function NavGroup({ title, items, onNavigate, variant = "default" }) {
    if (!items.length) return null;

    const itemClassActive = variant === "primary"
        ? "bg-primary text-primary-foreground shadow-sm"
        : "bg-primary text-primary-foreground";
    const itemClassIdle = variant === "primary"
        ? "text-foreground hover:bg-primary-soft"
        : variant === "muted"
            ? "text-muted-foreground hover:text-foreground hover:bg-primary-soft"
            : "text-foreground hover:bg-primary-soft";

    const paddingY = variant === "primary" ? "py-2.5" : "py-2";
    const iconSize = variant === "primary" ? "h-[18px] w-[18px]" : "h-4 w-4";
    const fontWeight = variant === "primary" ? "font-semibold" : "font-normal";

    return (
        <div className="mb-5">
            <p className="px-3 text-[10px] uppercase tracking-[0.15em] text-muted-foreground/70 font-semibold mb-1.5">
                {title}
            </p>
            <ul className="space-y-0.5">
                {items.map((it) => (
                    <li key={it.to}>
                        <NavLink
                            to={it.to}
                            onClick={onNavigate}
                            data-testid={`nav-${it.to.replace(/\//g, "") || "home"}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 ${paddingY} rounded-md text-sm ${fontWeight} transition-colors ${
                                    isActive ? itemClassActive : itemClassIdle
                                }`
                            }
                        >
                            <it.icon className={iconSize} />
                            <span>{it.label}</span>
                        </NavLink>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default function Sidebar({ onNavigate }) {
    const { user } = useAuth();
    if (!user) return null;
    const isOper = hasAnyRole(user, "operaciones");
    const isRecep = hasAnyRole(user, "recepcion");
    const isUE = hasAnyRole(user, "usuario_edificio");
    const master = isMaster(user);
    const coworking = isCoworking(user);

    const operItems = isOper ? [
        { to: "/comunicaciones", icon: Megaphone, label: "Comunicaciones" },
        { to: "/incidencias-kanban", icon: Kanban, label: "Incidencias" },
        { to: "/paqueteria", icon: Package, label: "Correo y paquetería" },
        { to: "/unidades-masters", icon: Users2, label: "Unidades & Responsables" },
        { to: "/reservas", icon: CalendarDays, label: "Reserva salas" },
        { to: "/configurar-salas", icon: SlidersHorizontal, label: "Configurar salas" },
        { to: "/recepcion-staff", icon: UserCog, label: "Recepción" },
    ] : [];

    const recepItems = isRecep ? [
        { to: "/visitas-hoy", icon: Ticket, label: "Visitas" },
        { to: "/paqueteria", icon: Package, label: "Correo y paquetería" },
        { to: "/comunicaciones", icon: Megaphone, label: "Comunicaciones" },
        { to: "/incidencias-kanban", icon: Wrench, label: "Incidencias" },
        { to: "/unidades-masters", icon: Users2, label: "Unidades & Responsables" },
        { to: "/reservas", icon: CalendarDays, label: "Reserva salas" },
    ] : [];

    // Usuario edificio: 4 secciones por intención
    const ueInicio = isUE ? [
        { to: "/inicio", icon: Home, label: "Inicio" },
    ] : [];

    const ueAcciones = isUE ? [
        { to: "/reservar", icon: CalendarDays, label: "Reservar sala" },
        { to: "/cargar-visita", icon: UserPlus, label: "Cargar visita" },
        { to: "/reportar-incidencia", icon: Wrench, label: "Reportar incidencia" },
    ] : [];

    const ueSeguimiento = isUE ? [
        { to: "/mis-reservas", icon: BookCheck, label: "Mis reservas" },
        { to: "/mis-visitas", icon: Ticket, label: "Mis visitas" },
        { to: "/mis-incidencias", icon: ClipboardList, label: "Mis incidencias" },
        { to: "/mis-paquetes", icon: Package, label: "Mis paquetes" },
    ] : [];

    const ueOtros = isUE ? [
        { to: "/bandeja-avisos", icon: Inbox, label: "Bandeja de avisos" },
        ...(master && !coworking ? [{ to: "/autorizados", icon: Users2, label: "Autorizados de mi unidad" }] : []),
    ] : [];

    return (
        <aside className="h-full flex flex-col bg-background">
            <div className="px-5 py-5 border-b border-border">
                {isUE ? (
                    <Link
                        to="/inicio"
                        onClick={onNavigate}
                        data-testid="sidebar-logo-home"
                        className="flex items-center gap-3 rounded-md -mx-1 px-1 py-0.5 hover:bg-primary-soft/60 transition-colors"
                    >
                        <img src="/foresta-icon.png" alt="Foresta" className="h-10 w-10 object-contain" />
                        <div className="leading-tight">
                            <p className="font-heading font-extrabold tracking-tight text-[15px] text-primary">Foresta</p>
                            <p className="text-[11px] text-muted-foreground">Reservas y Reclamos</p>
                        </div>
                    </Link>
                ) : (
                    <div className="flex items-center gap-3">
                        <img src="/foresta-icon.png" alt="Foresta" className="h-10 w-10 object-contain" />
                        <div className="leading-tight">
                            <p className="font-heading font-extrabold tracking-tight text-[15px] text-primary">Foresta</p>
                            <p className="text-[11px] text-muted-foreground">Reservas y Reclamos</p>
                        </div>
                    </div>
                )}
            </div>
            <nav className="flex-1 overflow-y-auto px-2 py-4">
                <NavGroup title="Operaciones" items={operItems} onNavigate={onNavigate} />
                <NavGroup title="Recepción" items={recepItems} onNavigate={onNavigate} />
                <NavGroup title="Inicio" items={ueInicio} onNavigate={onNavigate} variant="primary" />
                <NavGroup title="Acciones rápidas" items={ueAcciones} onNavigate={onNavigate} variant="primary" />
                <NavGroup title="Seguimiento" items={ueSeguimiento} onNavigate={onNavigate} variant="muted" />
                <NavGroup title="Otros" items={ueOtros} onNavigate={onNavigate} variant="muted" />
            </nav>
            <div className="px-3 py-3 border-t border-border text-[11px] text-muted-foreground flex items-center gap-2">
                <Settings className="h-3.5 w-3.5" /> Edificio Foresta · Mendoza
            </div>
        </aside>
    );
}
